import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Button } from 'reactstrap';
import { AsyncDisplay, LoadingMessage, Icon } from 'js/components';
import RubricListItem from './RubricListItem';
import TeamPageLayout from '../TeamPageLayout';
import styles from './TeamRubrics.module.scss';
import { useParams } from 'react-router-dom';

function NoFavoriteRubrics() {
  return (
    <div className="media text-muted mt-4 mb-4">
      <Icon
        icon={['fal', 'spider-web']}
        size="4x"
        className="mr-3"
        fixedWidth
      />
      <div className="media-body">
        <p>You don't have any favorite rubrics yet.</p>
        <p>Mark some rubrics as favorites and they'll appear here.</p>
      </div>
    </div>
  );
}

function NoRubrics() {
  const { workspaceId } = useParams();
  return (
    <div className="media text-muted mt-4 mb-4">
      <Icon
        icon={['fal', 'spider-web']}
        size="4x"
        className="mr-3"
        fixedWidth
      />
      <div className="media-body">
        <p>You haven't created any rubrics yet.</p>
        <p>
          <Link to={`/w/${workspaceId}/rubrics/new`}>Create a rubric</Link> to
          get started, then you'll be able to open it here to view this team's
          scores.
        </p>
        <Link to={`/w/${workspaceId}/rubrics/new`} className="btn btn-primary">
          <Icon icon="plus" className="btn-icon-left" /> Create a rubric
        </Link>
      </div>
    </div>
  );
}

function RubricList({
  workspaceId,
  teamId,
  rows,
  onToggleFavorite,
  renderNoRubricsMessage: RenderNoRubricsMessage,
  isFavoriteUpdating,
}) {
  return (
    <section className={styles.rubricSection}>
      {rows.length === 0 ? (
        <RenderNoRubricsMessage />
      ) : (
        <ul className={styles['rubric-list']}>
          {rows.map((row) => (
            <RubricListItem
              key={row.id}
              workspaceId={workspaceId}
              teamId={teamId}
              rubric={row}
              isFavoriteUpdating={isFavoriteUpdating}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

const OTHER_VIEW = {
  SUGGESTED: 'suggested',
  ALL: 'all',
};

function TeamRubricsComponent({
  workspaceId,
  teamId,
  team,
  teamConnectorConfigurations,
  teamConnectorConfigurationsHasLoaded,
  loaded,
  error,
  favorites,
  suggested,
  allRubrics,
  onToggleFavorite,
  isFavoriteUpdating,
}) {
  const [otherView, setOtherView] = useState(OTHER_VIEW.SUGGESTED);
  const resolvedOtherView = useMemo(
    () =>
      allRubrics.length === 0 || suggested.length === 0
        ? OTHER_VIEW.ALL
        : otherView,
    [allRubrics.length, suggested.length, otherView]
  );

  const lowerCardRows = useMemo(
    () => (resolvedOtherView === OTHER_VIEW.SUGGESTED ? suggested : allRubrics),
    [resolvedOtherView, suggested, allRubrics]
  );

  const handleToggleOtherView = useCallback(() => {
    setOtherView(
      otherView === OTHER_VIEW.SUGGESTED ? OTHER_VIEW.ALL : OTHER_VIEW.SUGGESTED
    );
  }, [otherView]);

  return (
    <TeamPageLayout
      team={team}
      teamConnectorConfigurations={teamConnectorConfigurations}
      teamConnectorConfigurationsHasLoaded={
        teamConnectorConfigurationsHasLoaded
      }
    >
      <AsyncDisplay isLoading={!loaded} isError={!!error}>
        <AsyncDisplay.Loading>
          <Card>
            <CardBody>
              <LoadingMessage iconSize="2x">
                Loading team rubrics&hellip;
              </LoadingMessage>
            </CardBody>
          </Card>
        </AsyncDisplay.Loading>
        <AsyncDisplay.Error>
          <Card>
            <CardBody>
              <p className="text-danger">
                Unable to load rubrics for this team.
              </p>
            </CardBody>
          </Card>
        </AsyncDisplay.Error>
        <AsyncDisplay.Resolved>
          {allRubrics.length > 0 && (
            <Card>
              <CardBody>
                <label className={styles['section-title']}>Favorites</label>
                <RubricList
                  workspaceId={workspaceId}
                  teamId={teamId}
                  rows={favorites}
                  onToggleFavorite={onToggleFavorite}
                  isFavoriteUpdating={isFavoriteUpdating}
                  renderNoRubricsMessage={NoFavoriteRubrics}
                />
              </CardBody>
            </Card>
          )}
          <Card>
            <CardBody>
              <div className={styles['section-header']}>
                <label className={styles['section-title']}>
                  {resolvedOtherView === OTHER_VIEW.SUGGESTED
                    ? 'Suggested Rubrics'
                    : 'All Rubrics'}
                </label>
                {allRubrics.length > 0 && (
                  <Button
                    color="link"
                    className={styles.toggle}
                    onClick={handleToggleOtherView}
                  >
                    {resolvedOtherView === OTHER_VIEW.SUGGESTED
                      ? 'Show all rubrics'
                      : 'Show suggested rubrics'}
                  </Button>
                )}
              </div>
              <RubricList
                workspaceId={workspaceId}
                teamId={teamId}
                rows={lowerCardRows}
                onToggleFavorite={onToggleFavorite}
                isFavoriteUpdating={isFavoriteUpdating}
                renderNoRubricsMessage={NoRubrics}
              />
            </CardBody>
          </Card>
        </AsyncDisplay.Resolved>
      </AsyncDisplay>
    </TeamPageLayout>
  );
}

export default TeamRubricsComponent;
